import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, ExternalLink } from "lucide-react";
import Reveal from "../components/common/Reveal";
import { VIDEO_LOGO_URL } from "../components/common/Brand";
import { proxied } from "../lib/assets";

const SAMPLE_ESCAPES = [
  {
    key: "iconic-africa",
    name: "Iconic Africa",
    region: "River Cruise",
    accent: "Safari elegance meets the Zambezi.",
    url: "https://travefy.com/website/Website-AfricaRiverCruise-Landing-ywtar32q4qexvq2",
    imageQuery: "african safari sunset elephant zambezi",
    imageUrl: "https://images.unsplash.com/photo-1516426122078-c23e76319801?w=1200&q=80",
  },
  {
    key: "french-riviera",
    name: "French Riviera",
    region: "Coastal Escape",
    accent: "Côte d'Azur, unhurried and iconic.",
    url: "https://travefy.com/website/VV_FrenchRivieraCruise-ywtar32q4eeurq2",
    imageUrl: "https://images.unsplash.com/photo-1533104816931-20fa691ff6ca?w=1200&q=80",
  },
  {
    key: "barcelona-mallorca",
    name: "Barcelona & Mallorca",
    region: "Mediterranean",
    accent: "Gaudí, sea breeze and sun-warmed stone.",
    url: "https://travefy.com/website/tropical-vacation-2024-tjuv2-jguv2-gjrcn-w2f-jdfr2-ywtar32q4tezzq2",
    imageUrl: "https://images.unsplash.com/photo-1583422409516-2895a77efded?w=1200&q=80",
  },
  {
    key: "arctic",
    name: "Arctic",
    region: "Expedition",
    accent: "The quiet, luminous edge of the world.",
    url: "https://travefy.com/website/tropical-vacation-2024-tjuv2-jguv2-gjrcn-w2f-7afrx-ywtar32q4ueqeq2",
    imageUrl: "https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=1200&q=80",
  },
  {
    key: "majestic-mountains",
    name: "Majestic Mountains",
    region: "Alpine Retreat",
    accent: "Peaks, pines and cinematic stillness.",
    url: "https://travefy.com/website/tropical-vacation-2024-tjuv2-jguv2-gjrcn-w2f-jjfrn-ywtar32q4qdrwq2",
    imageUrl: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1200&q=80",
  },
];

const TIERS = [
  {
    key: "easy",
    name: "The Easy Escape",
    emoji: "✈️",
    tagline: "Simple. Straightforward. Supported.",
    price: "Complimentary",
    priceSub: "Service",
    pitch: "Perfect for travelers who already have a general idea of what they want and just need help bringing it all together.",
    features: [
      "Booking of cruises, resorts, and vacation packages",
      "Access to preferred supplier perks and promotions",
      "Basic itinerary organization",
      "Email support leading up to travel",
    ],
    bestFor: "Quick getaways, all-inclusive resorts, and cruise bookings where most details are already packaged.",
  },
  {
    key: "elevated",
    name: "The Elevated Escape",
    emoji: "🌍",
    tagline: "Personalized. Thoughtful. Seamless.",
    price: "$149",
    priceSub: "per trip",
    pitch: "For travelers who want a more customized experience without handing over every detail.",
    features: [
      "Everything in the Easy Escape",
      "Personalized destination & experience recommendations",
      "Customized itinerary with hand-selected options",
      "Guidance on flights, transfers, and logistics",
      "Reservation assistance for key experiences",
      "Ongoing support before and during travel",
    ],
    bestFor: "Multi-stop trips, bucket list travel, or anyone who wants guidance and a more tailored experience.",
    featured: true,
  },
  {
    key: "signature",
    name: "The Signature Escape",
    emoji: "🥂",
    tagline: "Curated. Elevated. Effortless.",
    price: "$349",
    priceSub: "per trip",
    pitch: "This is where LuxeTini Escapes truly shines. A completely hands-off, high-touch experience from start to finish.",
    features: [
      "Everything in the Elevated Escape",
      "Fully customized, concierge-level itinerary design",
      "End-to-end trip management (flights, transfers, stays, experiences)",
      "Priority access to premium partners & exclusive amenities",
      "Dining, spa, and experience reservations handled",
      "Real-time support while you travel",
      "Advocacy and problem-solving if anything unexpected arises",
    ],
    bestFor: "Luxury travel, milestone trips, complex itineraries, or anyone who wants every detail handled flawlessly.",
  },
];

export default function Services() {
  return (
    <div data-testid="page-services">
      {/* Hero */}
      <section className="relative py-32 lg:py-44 px-6 lg:px-10 bg-nightsky overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover opacity-50"
          src={proxied("https://customer-assets.emergentagent.com/job_escape-design-lab/artifacts/303jhy5k_5537276_Coll_wavebreak_People_3840x2160.mp4")}
          poster="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?crop=entropy&cs=srgb&fm=jpg&w=2400&q=85"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          data-testid="services-hero-video"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/40 to-black/70" />
        <div className="relative z-10 max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-8">
            <div className="star-sep mb-8"><span className="text-white">Services</span></div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl font-light leading-[0.98] text-white" data-testid="services-title">
              Your Escape,<br /><span className="italic text-gold">your way.</span>
            </h1>
          </Reveal>
          <Reveal className="lg:col-span-4" delay={140}>
            <p className="text-lg text-white/85 leading-relaxed font-light">
              No two travelers — and no two escapes — are the same. Choose the level of service that meets you exactly where you are.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Brand video moment */}
      <section className="relative bg-nightsky overflow-hidden" data-testid="services-brand-video">
        <div className="relative max-w-[1500px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-6 order-2 lg:order-1">
            <div className="star-sep mb-6"><span>The Mark</span></div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory font-light leading-[1.05]">
              A signature of <span className="italic text-gold">precision & play.</span>
            </h2>
            <p className="mt-6 text-ivory/70 text-lg font-light leading-relaxed max-w-md">
              The compass points the way. The feathers give it soul. Behind every LuxeTini escape is a trip designed with the intention of a ritual and the ease of a cocktail hour.
            </p>
          </Reveal>
          <Reveal className="lg:col-span-6 order-1 lg:order-2" delay={120}>
            <div className="relative mx-auto max-w-lg">
              <video
                className="w-full h-auto"
                style={{ mixBlendMode: "screen" }}
                src={proxied(VIDEO_LOGO_URL)}
                poster={proxied("https://customer-assets.emergentagent.com/job_c911de1a-3013-4d29-a08e-7e9f9e5221e5/artifacts/gwvgxp6t_Untitled%20design.png")}
                autoPlay
                muted
                loop
                playsInline
                data-testid="services-video-logo"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Tiers */}
      <section className="py-16 lg:py-24 px-6 lg:px-10 bg-ivory">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="tier-grid">
          {TIERS.map((t, i) => (
            <Reveal key={t.key} delay={i * 120}>
              <div
                id={t.key}
                className={`scroll-mt-28 h-full flex flex-col p-10 border transition-colors ${
                  t.featured
                    ? "bg-ink text-ivory border-ink"
                    : "bg-ivory border-hairline hover:border-gold"
                }`}
                data-testid={`tier-${t.key}`}
              >
                <div className="text-xs uppercase tracking-[0.28em] text-gold">
                  {t.emoji} &nbsp;{t.priceSub}
                </div>
                <div className={`mt-2 font-serif text-4xl lg:text-5xl leading-tight ${t.featured ? "text-ivory" : "text-ink"}`}>
                  {t.price === "Complimentary" ? t.price : <>from <span className="italic">{t.price}</span></>}
                </div>

                <div className="mt-8">
                  <div className={`font-serif text-3xl ${t.featured ? "text-ivory" : "text-ink"}`}>{t.name}</div>
                  <div className={`mt-2 text-sm uppercase tracking-[0.22em] ${t.featured ? "text-gold" : "text-teal"}`}>{t.tagline}</div>
                </div>

                <p className={`mt-6 text-[15px] font-light leading-relaxed ${t.featured ? "text-ivory/75" : "text-muted"}`}>
                  {t.pitch}
                </p>

                <ul className="mt-8 space-y-3">
                  {t.features.map((f) => (
                    <li key={f} className="flex gap-3 items-start text-[15px] font-light leading-relaxed">
                      <Check size={16} strokeWidth={1.5} className={t.featured ? "text-gold mt-1 shrink-0" : "text-teal mt-1 shrink-0"} />
                      <span className={t.featured ? "text-ivory/85" : "text-ink/85"}>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className={`mt-8 pt-6 border-t ${t.featured ? "border-white/10" : "border-hairline"}`}>
                  <div className="text-xs uppercase tracking-[0.22em] mb-2 text-gold">Best for</div>
                  <p className={`text-sm font-light leading-relaxed ${t.featured ? "text-ivory/70" : "text-muted"}`}>
                    {t.bestFor}
                  </p>
                </div>

                <div className="mt-auto pt-8">
                  <Link
                    to={`/plan-your-escape?tier=${t.key}`}
                    className={`inline-flex items-center gap-2 h-12 px-6 text-[12px] uppercase tracking-[0.28em] transition-colors ${
                      t.featured
                        ? "bg-gold text-ink hover:bg-goldDeep"
                        : "bg-ink text-ivory hover:bg-teal"
                    }`}
                    data-testid={`tier-cta-${t.key}`}
                  >
                    Choose {t.name.replace("The ", "")}
                    <ArrowUpRight size={14} strokeWidth={1.5} />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Sample Escapes — curated Travefy trip previews */}
      <section id="sample-escapes" className="py-24 lg:py-32 px-6 lg:px-10 bg-[#F3EDE1] scroll-mt-24" data-testid="section-sample-escapes">
        <div className="max-w-[1500px] mx-auto">
          <Reveal>
            <div className="max-w-3xl mb-16">
              <div className="star-sep mb-6"><span>Sample Escapes</span></div>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink font-light leading-[1.05]">
                A glimpse at what an <span className="italic text-gold">Escape</span> can look like.
              </h2>
              <p className="mt-6 text-lg text-muted font-light leading-relaxed max-w-2xl">
                Real trip proposals I've thoughtfully designed — each one a starting point for your own bespoke escape.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="sample-escapes-grid">
            {SAMPLE_ESCAPES.map((esc, i) => (
              <Reveal key={esc.key} delay={i * 100}>
                <a
                  href={esc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative block h-[440px] overflow-hidden bg-ink"
                  data-testid={`sample-escape-${esc.key}`}
                >
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
                    style={{ backgroundImage: `url(${esc.imageUrl})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-nightsky via-nightsky/40 to-transparent" />
                  <div className="absolute inset-0 flex flex-col p-8">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-gold self-start bg-ink/40 backdrop-blur-sm px-3 py-1.5 border border-ivory/10">
                      {esc.region}
                    </div>
                    <div className="mt-auto">
                      <div className="font-serif text-3xl lg:text-4xl text-ivory leading-tight">{esc.name}</div>
                      <p className="mt-3 text-sm text-ivory/80 font-light italic leading-relaxed">{esc.accent}</p>
                      <div className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-gold group-hover:text-ivory transition-colors">
                        View Itinerary
                        <ArrowUpRight size={14} strokeWidth={1.5} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </div>
                    </div>
                  </div>
                </a>
              </Reveal>
            ))}
            {/* Trailing invitation card */}
            <Reveal delay={SAMPLE_ESCAPES.length * 100}>
              <div className="h-[440px] border border-hairline bg-ivory p-8 flex flex-col" data-testid="sample-escapes-invite">
                <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Yours to design</div>
                <h3 className="mt-6 font-serif text-3xl lg:text-4xl text-ink leading-tight">
                  Your escape <span className="italic text-gold">starts here.</span>
                </h3>
                <p className="mt-6 text-sm text-muted font-light leading-relaxed">
                  Every sample above began as a conversation. Let's begin yours — I'll craft a proposal shaped entirely around what matters to you.
                </p>
                <div className="mt-auto pt-6 flex flex-col gap-3">
                  <Link
                    to="/plan-your-escape"
                    className="inline-flex items-center justify-between h-12 px-5 bg-ink text-ivory hover:bg-teal transition-colors text-[11px] uppercase tracking-[0.28em]"
                    data-testid="sample-escapes-cta"
                  >
                    Design My Escape <ArrowUpRight size={14} strokeWidth={1.5} />
                  </Link>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted">
                    <ExternalLink size={11} strokeWidth={1.5} />
                    <span>Sample itineraries open on Travefy</span>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <section className="py-24 lg:py-32 px-6 lg:px-10">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <div className="star-sep justify-center mb-8"><span>Not Sure?</span></div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05]">
              Not sure which <span className="italic text-gold">Escape</span> fits you?
            </h2>
            <p className="mt-8 text-lg text-muted font-light leading-relaxed">
              That's exactly what I'm here for. Whether you're just starting to dream or ready to book, I'll guide you to the level of service that makes the most sense for your trip — and your peace of mind.
            </p>
            <Link
              to="/plan-your-escape"
              className="mt-10 inline-flex items-center gap-3 h-14 px-10 bg-gold text-ink hover:bg-goldDeep transition-colors text-[12px] uppercase tracking-[0.28em]"
              data-testid="services-bottom-cta"
            >
              Start Planning Your Escape <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
